import React, { useState, useCallback, useEffect } from 'react';
import { View, StyleSheet, ScrollView, RefreshControl, Text } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import FeaturedFeedPost from '../feed/FeaturedFeedPost';
import LinkPost from '../feed/LinkPost';
import FeedPostSkeleton from '../common/FeedPostSkeleton';
import { getClubPosts } from '../../lib/api/clubs';
import { ClubsStackParamList } from '../../types/navigation';

interface ClubFeedTabProps {
  clubId: string;
  isAdmin: boolean;
  allowedPostingDays?: number[];
  onCreatePost?: () => void;
}

interface ClubPost {
  id: string;
  type: 'text' | 'image' | 'poll' | 'link';
  userId: string;
  user: {
    id: string;
    name: string;
    photo?: string;
    profession?: string;
  };
  text?: string;
  imageUrl?: string;
  linkUrl?: string;
  likesCount: number;
  commentsCount: number;
  createdAt: string;
}

type FeedNavigationProp = StackNavigationProp<ClubsStackParamList>;

// Helper function to format numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

const ClubFeedTab: React.FC<ClubFeedTabProps> = ({ clubId, isAdmin, allowedPostingDays = [], onCreatePost }) => {
  const { theme } = useTheme();
  const [posts, setPosts] = useState<ClubPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInitialSkeleton, setShowInitialSkeleton] = useState(true);

  // Check if posting is allowed today
  const currentDay = new Date().getDay();
  const isPostingAllowedToday = allowedPostingDays.length === 0 || allowedPostingDays.includes(currentDay);
  
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const allowedDayNames = allowedPostingDays.length > 0 
    ? allowedPostingDays.map(d => dayNames[d]).join(', ')
    : null;

  const loadPosts = useCallback(async (isRefresh: boolean = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
        setShowInitialSkeleton(true);
      }
      
      setError(null);
      
      const response = await getClubPosts(clubId);
      setPosts(response);
      
      setTimeout(() => setShowInitialSkeleton(false), 200);
    } catch (err) {
      console.error('Error loading club posts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load posts');
      setShowInitialSkeleton(false);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [clubId]);

  const onRefresh = useCallback(() => {
    loadPosts(true);
  }, [loadPosts]);

  const handleLike = (postId: string, isLiked: boolean) => {
    // TODO: Implement like functionality when backend supports it
    console.log('Like clicked:', postId, isLiked);
  };

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // Refresh posts when screen comes into focus (e.g., after creating a post)
  useFocusEffect(
    useCallback(() => {
      console.log('[ClubFeedTab] Screen focused, refreshing posts...');
      loadPosts(true);
    }, [loadPosts])
  );

  return (
    <View style={styles.container}>
      {showInitialSkeleton && (
        <View style={[StyleSheet.absoluteFillObject, { zIndex: 10, backgroundColor: theme.background }]}>
          <View style={styles.screen}>
            <FeedPostSkeleton />
            <FeedPostSkeleton />
            <FeedPostSkeleton />
          </View>
        </View>
      )}
      {error && posts.length === 0 ? (
        <View style={[styles.centerContainer, { backgroundColor: theme.background }]}>
          <Text style={[styles.errorText, { color: theme.error }]}>
            {error}
          </Text>
          <Text 
            style={[styles.retryText, { color: theme.primary }]}
            onPress={() => loadPosts()}
          >
            Tap to retry
          </Text>
        </View>
      ) : (
        <ScrollView
          style={[styles.screen, { backgroundColor: theme.background }]}
          showsVerticalScrollIndicator={true}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={theme.primary}
              colors={[theme.primary]}
            />
          }
        >
          {posts.map((post, index) => {
            const isLast = index === posts.length - 1;
            
            if (post.type === 'text' || post.type === 'image') {
              // Debug log to check image URL
              if (post.type === 'image') {
                console.log('[ClubFeedTab] Image post:', {
                  id: post.id,
                  imageUrl: post.imageUrl,
                  type: post.type
                });
              }
              
              return (
                <FeaturedFeedPost
                  key={post.id}
                  post={{
                    id: post.id,
                    userId: post.userId,
                    user: post.user.name || 'Anonymous',
                    location: post.user.profession || '',
                    avatar: post.user.photo || '',
                    caption: post.text || '',
                    image: post.imageUrl,
                    likes: formatNumber(post.likesCount),
                    comments: formatNumber(post.commentsCount),
                    isLiked: false, // TODO: Add like functionality
                  }}
                  onLike={() => handleLike(post.id, false)}
                  style={!isLast ? styles.postSpacing : undefined}
                />
              );
            } else if (post.type === 'link' && post.linkUrl) {
              return (
                <LinkPost
                  key={post.id}
                  post={{
                    id: post.id,
                    userId: post.userId,
                    user: post.user.name || 'Anonymous',
                    location: post.user.profession || '',
                    avatar: post.user.photo || '',
                    caption: post.text || '',
                    link: post.linkUrl,
                    likes: formatNumber(post.likesCount),
                    comments: formatNumber(post.commentsCount),
                  }}
                  style={!isLast ? styles.postSpacing : undefined}
                />
              );
            }
            return null;
          })}
          
          {posts.length === 0 && !loading && (
            <View style={styles.emptyContainer}>
              <Text style={[styles.emptyText, { color: theme.textSecondary }]}>
                No posts yet. Be the first to share!
              </Text>
            </View>
          )}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  screen: {
    flex: 1,
    paddingHorizontal: 20,
    paddingVertical: 32,
  },
  postSpacing: {
    marginBottom: 48,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 12,
  },
  retryText: {
    fontSize: 16,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 16,
    textAlign: 'center',
  },
});

export default ClubFeedTab;
